import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { 
  Lock, ArrowLeft, Package, ShoppingBag, Plus, Trash2, Edit, 
  X, Save, Image, ChevronDown, ChevronUp, Upload, Ticket, ToggleLeft, ToggleRight,
  Clock, Settings, FileSpreadsheet, Calendar, TrendingUp, DollarSign
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShopAdmin() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Order stats filter
  const [statsDateFrom, setStatsDateFrom] = useState("");
  const [statsDateTo, setStatsDateTo] = useState("");
  
  // Excel export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportProducts, setExportProducts] = useState([]);
  
  // Order editing state
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderEditForm, setOrderEditForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    notes: ""
  });
  
  // Shop settings state
  const [shopSettings, setShopSettings] = useState({
    order_deadline: "",
    order_deadline_enabled: false
  });

  // Check if user is already logged in as admin
  useEffect(() => {
    const checkAdminAuth = async () => {
      const compToken = localStorage.getItem("comp_token");
      if (compToken && compToken !== "undefined" && compToken !== "null") {
        try {
          const res = await axios.get(`${API}/competition/me?token=${compToken}`);
          if (res.data && res.data.is_admin) {
            // User is admin, auto-login to shop admin
            const adminRes = await axios.post(`${API}/admin/auto-login?token=${compToken}`);
            if (adminRes.data.success && adminRes.data.token) {
              setToken(adminRes.data.token);
              fetchProducts(adminRes.data.token);
              fetchOrders(adminRes.data.token);
              fetchCoupons(adminRes.data.token);
              fetchShopSettings();
            }
          }
        } catch (err) {
          console.log("Not logged in as admin, showing password form");
        }
      }
      setCheckingAuth(false);
    };
    checkAdminAuth();
  }, []);

  // Product form state
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    washing_instructions: "",
    image_url: "",
    gallery_images: [],
    size_chart_images: [],
    base_price: 0,
    purchase_price: "",
    stock: "",
    is_active: true,
    custom_name_enabled: false,
    custom_name_price: 299,
    custom_name_max_length: 15,
    variation_groups: []
  });

  // Coupon form state
  const [couponForm, setCouponForm] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: 0,
    min_order_amount: 0,
    max_uses: 0,
    valid_until: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setToken(response.data.token);
        fetchProducts(response.data.token);
        fetchOrders(response.data.token);
        fetchCoupons(response.data.token);
        fetchShopSettings();
      } else {
        setError("Hibás jelszó!");
      }
    } catch (err) {
      setError("Hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (adminToken) => {
    try {
      const response = await axios.get(`${API}/products?active_only=false`);
      setProducts(response.data);
    } catch (err) {
      console.error("Failed to fetch products");
    }
  };

  const fetchOrders = async (adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/orders?token=${adminToken}`);
      setOrders(response.data);
    } catch (err) {
      console.error("Failed to fetch orders");
    }
  };

  const fetchCoupons = async (adminToken) => {
    try {
      const response = await axios.get(`${API}/admin/coupons?token=${adminToken}`);
      setCoupons(response.data);
    } catch (err) {
      console.error("Failed to fetch coupons");
    }
  };

  const fetchShopSettings = async () => {
    try {
      const response = await axios.get(`${API}/shop/settings`);
      if (response.data) {
        setShopSettings({
          order_deadline: response.data.order_deadline ? response.data.order_deadline.slice(0, 16) : "",
          order_deadline_enabled: response.data.order_deadline_enabled || false
        });
      }
    } catch (err) {
      console.error("Failed to fetch shop settings");
    }
  };

  const handleSaveShopSettings = async () => {
    try {
      const deadline = shopSettings.order_deadline ? new Date(shopSettings.order_deadline).toISOString() : null;
      await axios.put(
        `${API}/admin/shop/settings?token=${token}&order_deadline=${deadline || ''}&order_deadline_enabled=${shopSettings.order_deadline_enabled}`
      );
      toast.success("Beállítások mentve!");
    } catch (err) {
      toast.error("Hiba történt a mentéskor.");
    }
  };

  const handleLogout = () => {
    setToken("");
    setProducts([]);
    setOrders([]);
    setCoupons([]);
  };

  const resetProductForm = () => {
    setProductForm({
      name: "",
      description: "",
      washing_instructions: "",
      image_url: "",
      gallery_images: [],
      size_chart_images: [],
      base_price: 0,
      purchase_price: "",
      stock: "",
      is_active: true,
      custom_name_enabled: false,
      custom_name_price: 299,
      custom_name_max_length: 15,
      variation_groups: []
    });
    setEditingProduct(null);
  };

  const resetCouponForm = () => {
    setCouponForm({
      code: "",
      discount_type: "percentage",
      discount_value: 0,
      min_order_amount: 0,
      max_uses: 0,
      valid_until: ""
    });
    setEditingCoupon(null);
  };

  const handleEditProduct = (product) => {
    setProductForm({
      name: product.name,
      description: product.description || "",
      washing_instructions: product.washing_instructions || "",
      image_url: product.image_url || "",
      gallery_images: product.gallery_images || [],
      size_chart_images: product.size_chart_images || [],
      base_price: product.base_price || 0,
      purchase_price: product.purchase_price !== null && product.purchase_price !== undefined ? product.purchase_price : "",
      stock: product.stock !== null && product.stock !== undefined ? product.stock : "",
      is_active: product.is_active !== false,
      custom_name_enabled: product.custom_name_enabled || false,
      custom_name_price: product.custom_name_price || 299,
      custom_name_max_length: product.custom_name_max_length || 15,
      variation_groups: product.variation_groups || []
    });
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    
    if (!productForm.name.trim()) {
      toast.error("A termék neve kötelező!");
      return;
    }

    // Prepare data with proper stock value
    const dataToSend = {
      ...productForm,
      stock: productForm.stock === "" ? null : parseInt(productForm.stock),
      purchase_price: productForm.purchase_price === "" ? null : parseInt(productForm.purchase_price)
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}?token=${token}`, dataToSend);
        toast.success("Termék frissítve!");
      } else {
        await axios.post(`${API}/admin/products?token=${token}`, dataToSend);
        toast.success("Termék létrehozva!");
      }
      
      fetchProducts(token);
      setShowProductForm(false);
      resetProductForm();
    } catch (err) {
      toast.error("Hiba történt a mentéskor.");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Biztosan törölni szeretnéd ezt a terméket?")) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}?token=${token}`);
      toast.success("Termék törölve!");
      fetchProducts(token);
    } catch (err) {
      toast.error("Hiba történt a törléskor.");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status, pickupLocation = "") => {
    try {
      let url = `${API}/admin/orders/${orderId}/status?token=${token}&status=${status}`;
      if (pickupLocation) {
        url += `&pickup_location=${pickupLocation}`;
      }
      await axios.put(url);
      toast.success("Státusz frissítve!");
      fetchOrders(token);
    } catch (err) {
      toast.error("Hiba történt.");
    }
  };

  const handleSetReadyPickup = (orderId, pickupLocation) => {
    handleUpdateOrderStatus(orderId, "ready_pickup", pickupLocation);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setOrderEditForm({
      customer_name: order.customer_name || "",
      customer_email: order.customer_email || "",
      customer_phone: order.customer_phone || "",
      customer_address: order.customer_address || "",
      notes: order.notes || ""
    });
  };

  const handleSaveOrder = async () => {
    try {
      await axios.put(`${API}/admin/orders/${editingOrder.id}?token=${token}`, orderEditForm);
      toast.success("Rendelés frissítve!");
      setEditingOrder(null);
      fetchOrders(token);
    } catch (err) {
      toast.error("Hiba történt a mentéskor.");
    }
  };

  const handleProcessExpiredOrders = async () => {
    try {
      const response = await axios.post(`${API}/admin/process-expired-orders?token=${token}`);
      const { processed, cancelled, message } = response.data;
      if (processed > 0 || cancelled > 0) {
        toast.success(message);
        fetchOrders(token);
      } else {
        toast.info(message);
      }
    } catch (err) {
      toast.error("Hiba történt a feldolgozás során.");
    }
  };

  // Calculate order statistics
  const calculateOrderStats = () => {
    let filteredOrders = orders.filter(o => o.status !== "deleted" && o.status !== "törölve" && o.status !== "cancelled");
    
    // Apply date filter
    if (statsDateFrom) {
      const fromDate = new Date(statsDateFrom);
      filteredOrders = filteredOrders.filter(o => new Date(o.created_at) >= fromDate);
    }
    if (statsDateTo) {
      const toDate = new Date(statsDateTo);
      toDate.setHours(23, 59, 59, 999);
      filteredOrders = filteredOrders.filter(o => new Date(o.created_at) <= toDate);
    }
    
    let revenue = 0;
    let totalCost = 0;
    
    filteredOrders.forEach(order => {
      // Use total_amount if available, otherwise fall back to total_price (for old orders)
      revenue += order.total_amount || order.total_price || 0;
      
      // Calculate cost using purchase_price from the product
      // The purchase_price applies retroactively to all orders
      order.items?.forEach(item => {
        const product = products.find(p => p.id === item.product_id);
        // Use purchase_price if set, otherwise use 0 (no cost = 100% profit for items without purchase price)
        const purchasePrice = product?.purchase_price !== null && product?.purchase_price !== undefined 
          ? product.purchase_price 
          : 0; // If no purchase price, assume 0 cost
        totalCost += purchasePrice * item.quantity;
      });
    });
    
    const profit = revenue - totalCost;
    
    return { revenue, totalCost, profit, orderCount: filteredOrders.length };
  };

  // Generate Excel export
  const generateExcelExport = () => {
    let filteredOrders = orders.filter(o => o.status !== "deleted" && o.status !== "törölve");
    
    // Apply date filter
    if (exportDateFrom) {
      const fromDate = new Date(exportDateFrom);
      filteredOrders = filteredOrders.filter(o => new Date(o.created_at) >= fromDate);
    }
    if (exportDateTo) {
      const toDate = new Date(exportDateTo);
      toDate.setHours(23, 59, 59, 999);
      filteredOrders = filteredOrders.filter(o => new Date(o.created_at) <= toDate);
    }
    
    // Apply product filter
    if (exportProducts.length > 0) {
      filteredOrders = filteredOrders.filter(o => 
        o.items?.some(item => exportProducts.includes(item.product_id))
      );
    }
    
    // Group items by product name and variations
    const productGroups = {};
    
    filteredOrders.forEach(order => {
      order.items?.forEach(item => {
        // Skip if product filter is active and this product is not selected
        if (exportProducts.length > 0 && !exportProducts.includes(item.product_id)) return;
        
        const productName = item.product_name;
        if (!productGroups[productName]) {
          productGroups[productName] = {};
        }
        
        // Build variation key
        const variationKey = Object.entries(item.variation_selections || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ") || "Alap";
        
        if (!productGroups[productName][variationKey]) {
          productGroups[productName][variationKey] = [];
        }
        
        // Add items
        for (let i = 0; i < item.quantity; i++) {
          productGroups[productName][variationKey].push({
            customName: item.custom_name || null,
            size: item.variation_selections?.["Méret"] || item.variation_selections?.["méret"] || null
          });
        }
      });
    });
    
    // Generate CSV content
    let csvContent = "Termék;Variáció;Összesítés\n";
    
    Object.entries(productGroups).forEach(([productName, variations]) => {
      Object.entries(variations).forEach(([variationKey, items]) => {
        // Group by size and custom name
        const sizeGroups = {};
        items.forEach(item => {
          const key = item.customName ? `${item.size || 'N/A'} - ${item.customName}` : (item.size || 'N/A');
          if (!sizeGroups[key]) {
            sizeGroups[key] = { count: 0, hasCustomName: !!item.customName };
          }
          sizeGroups[key].count++;
        });
        
        const summary = Object.entries(sizeGroups)
          .map(([key, data]) => {
            if (data.hasCustomName) {
              return key; // Already includes custom name
            }
            return data.count > 1 ? `${key} ${data.count}db` : key;
          })
          .join(", ");
        
        csvContent += `"${productName}";"${variationKey}";"${summary}"\n`;
      });
    });
    
    // Download CSV
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `rendelesek_${exportDateFrom || 'osszes'}_${exportDateTo || 'osszes'}.csv`;
    link.click();
    
    setShowExportModal(false);
    toast.success("Export letöltve!");
  };

  // Image upload handlers
  const handleProductImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("token", token);
    
    try {
      const response = await axios.post(`${API}/admin/products/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProductForm({ ...productForm, image_url: response.data.url });
      toast.success("Kép feltöltve!");
    } catch (err) {
      toast.error("Hiba a kép feltöltésekor.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVariationImageUpload = async (file, groupIndex, optionIndex) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("token", token);
    
    try {
      const response = await axios.post(`${API}/admin/products/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      updateVariationOption(groupIndex, optionIndex, 'image_url', response.data.url);
      toast.success("Variáció kép feltöltve!");
    } catch (err) {
      toast.error("Hiba a kép feltöltésekor.");
    }
  };

  const handleGalleryImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("token", token);
    
    try {
      const response = await axios.post(`${API}/admin/products/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProductForm({ 
        ...productForm, 
        gallery_images: [...productForm.gallery_images, response.data.url] 
      });
      toast.success("Galéria kép feltöltve!");
    } catch (err) {
      toast.error("Hiba a kép feltöltésekor.");
    } finally {
      setUploadingImage(false);
    }
  };

  const removeGalleryImage = (index) => {
    const newGallery = productForm.gallery_images.filter((_, i) => i !== index);
    setProductForm({ ...productForm, gallery_images: newGallery });
  };

  // Size chart image handlers
  const handleSizeChartImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("token", token);
    
    try {
      const response = await axios.post(`${API}/admin/products/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setProductForm({ 
        ...productForm, 
        size_chart_images: [...productForm.size_chart_images, response.data.url] 
      });
      toast.success("Mérettáblázat kép feltöltve!");
    } catch (err) {
      toast.error("Hiba a kép feltöltésekor.");
    } finally {
      setUploadingImage(false);
    }
  };

  const removeSizeChartImage = (index) => {
    const newImages = productForm.size_chart_images.filter((_, i) => i !== index);
    setProductForm({ ...productForm, size_chart_images: newImages });
  };

  // Coupon handlers
  const handleEditCoupon = (coupon) => {
    setCouponForm({
      code: coupon.code,
      discount_type: coupon.discount_type || "percentage",
      discount_value: coupon.discount_value || 0,
      min_order_amount: coupon.min_order_amount || 0,
      max_uses: coupon.max_uses || 0,
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : ""
    });
    setEditingCoupon(coupon);
    setShowCouponForm(true);
  };

  const handleSaveCoupon = async (e) => {
    e.preventDefault();
    
    if (!couponForm.code.trim()) {
      toast.error("A kuponkód kötelező!");
      return;
    }

    try {
      const data = {
        ...couponForm,
        valid_until: couponForm.valid_until ? new Date(couponForm.valid_until).toISOString() : null
      };
      
      if (editingCoupon) {
        await axios.put(`${API}/admin/coupons/${editingCoupon.id}?token=${token}`, data);
        toast.success("Kupon frissítve!");
      } else {
        await axios.post(`${API}/admin/coupons?token=${token}`, data);
        toast.success("Kupon létrehozva!");
      }
      
      fetchCoupons(token);
      setShowCouponForm(false);
      resetCouponForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba történt a mentéskor.");
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm("Biztosan törölni szeretnéd ezt a kupont?")) return;
    
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}?token=${token}`);
      toast.success("Kupon törölve!");
      fetchCoupons(token);
    } catch (err) {
      toast.error("Hiba történt a törléskor.");
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const response = await axios.put(`${API}/admin/coupons/${couponId}/toggle?token=${token}`);
      toast.success(response.data.is_active ? "Kupon aktiválva!" : "Kupon inaktiválva!");
      fetchCoupons(token);
    } catch (err) {
      toast.error("Hiba történt.");
    }
  };

  // Variation group helpers
  const addVariationGroup = () => {
    setProductForm({
      ...productForm,
      variation_groups: [
        ...productForm.variation_groups,
        { id: Date.now().toString(), name: "", options: [] }
      ]
    });
  };

  const updateVariationGroup = (index, field, value) => {
    const groups = [...productForm.variation_groups];
    groups[index][field] = value;
    setProductForm({ ...productForm, variation_groups: groups });
  };

  const removeVariationGroup = (index) => {
    const groups = [...productForm.variation_groups];
    groups.splice(index, 1);
    setProductForm({ ...productForm, variation_groups: groups });
  };

  const addVariationOption = (groupIndex) => {
    const groups = [...productForm.variation_groups];
    groups[groupIndex].options.push({
      id: Date.now().toString(),
      name: "",
      price: productForm.base_price,
      image_url: "",
      stock: null
    });
    setProductForm({ ...productForm, variation_groups: groups });
  };

  const updateVariationOption = (groupIndex, optionIndex, field, value) => {
    const groups = [...productForm.variation_groups];
    groups[groupIndex].options[optionIndex][field] = value;
    setProductForm({ ...productForm, variation_groups: groups });
  };

  const removeVariationOption = (groupIndex, optionIndex) => {
    const groups = [...productForm.variation_groups];
    groups[groupIndex].options.splice(optionIndex, 1);
    setProductForm({ ...productForm, variation_groups: groups });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('hu-HU').format(price) + ' Ft';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status, pickupLocation) => {
    const labels = {
      order_received: "Rendelés felvéve",
      payment_pending: "Fizetés folyamatban",
      paid: "Fizetett",
      payment_failed: "Fizetés sikertelen",
      preparing: "Rendelés készülőben",
      ready_pickup: "Átvehető",
      completed: "Átvette",
      cancelled: "Törölve"
    };
    let label = labels[status] || status;
    if (status === "ready_pickup") {
      // Always show both names for ready_pickup
      label = "Átvehető";
    }
    return label;
  };

  const getStatusColor = (status) => {
    const colors = {
      order_received: "bg-blue-500/20 text-blue-400",
      payment_pending: "bg-yellow-500/20 text-yellow-400",
      paid: "bg-green-500/20 text-green-400",
      payment_failed: "bg-red-500/20 text-red-400",
      preparing: "bg-purple-500/20 text-purple-400",
      ready_pickup: "bg-[#D4AF37]/20 text-[#D4AF37]",
      completed: "bg-emerald-500/20 text-emerald-400",
      cancelled: "bg-red-500/20 text-red-400"
    };
    return colors[status] || "bg-gray-500/20 text-gray-400";
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/60">Bejelentkezés ellenőrzése...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-md">
          <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-[#D4AF37]" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white text-center mb-2">Shop Admin</h1>
            <p className="text-white/60 text-center mb-8">Termékek és rendelések kezelése</p>
            
            <form onSubmit={handleLogin}>
              <Input
                type="password"
                placeholder="Jelszó"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mb-4 bg-[#0A0A0A] border-[rgba(212,175,55,0.3)] text-white"
              />
              {error && <p className="text-red-500 text-sm mb-4 text-center">{error}</p>}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
              >
                {loading ? "..." : "Belépés"}
              </Button>
            </form>
            
            <button
              onClick={() => navigate("/shop")}
              className="flex items-center justify-center gap-2 mt-6 text-white/40 hover:text-[#D4AF37] transition-colors text-sm w-full"
            >
              <ArrowLeft size={16} />
              Vissza a shopba
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-4 md:p-8">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/shop")}
              className="text-white/40 hover:text-white"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl font-bold text-white">Shop Admin</h1>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
          >
            Kilépés
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab("products")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "products"
                ? "bg-[#D4AF37] text-[#0A0A0A]"
                : "bg-[#141414] text-white/60 hover:text-white"
            }`}
          >
            <Package size={18} />
            Termékek ({products.length})
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "orders"
                ? "bg-[#D4AF37] text-[#0A0A0A]"
                : "bg-[#141414] text-white/60 hover:text-white"
            }`}
          >
            <ShoppingBag size={18} />
            Rendelések ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab("coupons")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "coupons"
                ? "bg-[#D4AF37] text-[#0A0A0A]"
                : "bg-[#141414] text-white/60 hover:text-white"
            }`}
          >
            <Ticket size={18} />
            Kuponok ({coupons.length})
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "settings"
                ? "bg-[#D4AF37] text-[#0A0A0A]"
                : "bg-[#141414] text-white/60 hover:text-white"
            }`}
          >
            <Settings size={18} />
            Beállítások
          </button>
        </div>

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Clock size={24} className="text-[#D4AF37]" />
              Rendelési határidő
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-white font-medium">Határidő engedélyezése</label>
                  <p className="text-white/40 text-sm">Ha be van kapcsolva, a Shop oldalon megjelenik a visszaszámláló</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShopSettings({...shopSettings, order_deadline_enabled: !shopSettings.order_deadline_enabled})}
                  className={`p-2 rounded-lg border transition-colors ${
                    shopSettings.order_deadline_enabled
                      ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                      : "border-white/20 text-white/40"
                  }`}
                >
                  {shopSettings.order_deadline_enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                </button>
              </div>
              
              {shopSettings.order_deadline_enabled && (
                <div className="bg-[#0A0A0A] p-4 rounded-lg">
                  <label className="text-white/60 text-sm mb-2 block">Rendelési határidő dátuma és időpontja</label>
                  <Input
                    type="datetime-local"
                    value={shopSettings.order_deadline}
                    onChange={(e) => setShopSettings({...shopSettings, order_deadline: e.target.value})}
                    className="bg-[#141414] border-white/20 text-white"
                  />
                  <p className="text-white/40 text-xs mt-2">
                    A határidő után 24 óráig még lehet rendelni, de csak banki átutalással. 
                    Utána a rendelés teljesen lezárul.
                  </p>
                </div>
              )}
              
              <Button
                onClick={handleSaveShopSettings}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
              >
                <Save size={18} className="mr-2" />
                Beállítások mentése
              </Button>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === "products" && (
          <>
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => {
                  resetProductForm();
                  setShowProductForm(true);
                }}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
              >
                <Plus size={18} className="mr-2" />
                Új termék
              </Button>
            </div>

            {/* Product Form Modal */}
            {showProductForm && (
              <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
                <div className="min-h-screen px-4 py-8 flex items-start justify-center">
                  <div className="bg-[#141414] rounded-lg max-w-2xl w-full relative">
                    <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#141414] z-10 rounded-t-lg">
                      <h2 className="text-xl font-bold text-white">
                        {editingProduct ? "Termék szerkesztése" : "Új termék"}
                      </h2>
                      <button
                        onClick={() => {
                          setShowProductForm(false);
                          resetProductForm();
                        }}
                        className="text-white/60 hover:text-white"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  
                    <form onSubmit={handleSaveProduct} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Termék neve *</label>
                        <Input
                          required
                          value={productForm.name}
                          onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                          className="bg-[#0A0A0A] border-white/20 text-white"
                          placeholder="pl. Gepárd nyeregborító"
                        />
                      </div>

                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Termék kép (opcionális)</label>
                        <div className="space-y-3">
                          {productForm.image_url && (
                            <div className="flex items-center gap-3">
                              <div className="w-20 h-20 bg-[#0A0A0A] rounded overflow-hidden flex-shrink-0">
                                <img
                                  src={productForm.image_url.startsWith("/api/") ? `${BACKEND_URL}${productForm.image_url}` : productForm.image_url}
                                  alt="Előnézet"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => setProductForm({...productForm, image_url: ""})}
                                className="text-red-400 text-xs hover:text-red-300"
                              >
                                Kép eltávolítása
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleProductImageUpload(e.target.files[0])}
                              className="hidden"
                              id="product-image-upload"
                            />
                            <label
                              htmlFor="product-image-upload"
                              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-dashed cursor-pointer transition-colors ${
                                uploadingImage 
                                  ? "border-white/20 bg-white/5 text-white/40" 
                                  : "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                              }`}
                            >
                              <Upload size={18} />
                              {uploadingImage ? "Feltöltés..." : "Feltöltés"}
                            </label>
                            <span className="text-white/40 text-sm">vagy</span>
                          </div>
                          <div>
                            <Input
                              type="url"
                              value={productForm.image_url.startsWith("/api/") ? "" : productForm.image_url}
                              onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                              className="bg-[#0A0A0A] border-white/20 text-white"
                              placeholder="Kép URL beillesztése (https://...)"
                            />
                            <p className="text-white/40 text-xs mt-1">💡 URL használata tartós - deploy után is látszik!</p>
                          </div>
                        </div>
                      </div>

                    {/* Gallery Images */}
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Galéria képek (alképek)</label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {productForm.gallery_images.map((img, index) => (
                          <div key={index} className="relative w-16 h-16 bg-[#0A0A0A] rounded overflow-hidden group">
                            <img
                              src={img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img}
                              alt={`Galéria ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        <div className="w-16 h-16">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleGalleryImageUpload(e.target.files[0])}
                            className="hidden"
                            id="gallery-image-upload"
                          />
                          <label
                            htmlFor="gallery-image-upload"
                            className="w-full h-full flex items-center justify-center border border-dashed border-white/30 rounded cursor-pointer hover:border-[#D4AF37] transition-colors"
                          >
                            <Plus size={20} className="text-white/40" />
                          </label>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="url"
                          id="gallery-url-input"
                          className="bg-[#0A0A0A] border-white/20 text-white flex-1"
                          placeholder="Galéria kép URL (https://...)"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const url = e.target.value.trim();
                              if (url && url.startsWith('http')) {
                                setProductForm({
                                  ...productForm,
                                  gallery_images: [...productForm.gallery_images, url]
                                });
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-[#D4AF37] text-[#D4AF37]"
                          onClick={() => {
                            const input = document.getElementById('gallery-url-input');
                            const url = input.value.trim();
                            if (url && url.startsWith('http')) {
                              setProductForm({
                                ...productForm,
                                gallery_images: [...productForm.gallery_images, url]
                              });
                              input.value = '';
                            }
                          }}
                        >
                          Hozzáad
                        </Button>
                      </div>
                      <p className="text-white/40 text-xs mt-1">💡 URL-ek tartósak - deploy után is látszanak!</p>
                    </div>

                    {/* Size Chart Images Section */}
                    <div className="border-t border-white/10 pt-4">
                      <label className="text-white/60 text-sm mb-2 block">📏 Mérettáblázat képek</label>
                      <p className="text-white/40 text-xs mb-3">
                        Itt töltheted fel a mérettáblázat képeket. Ezek a leírás alatt fognak megjelenni a vásárlóknak.
                      </p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {productForm.size_chart_images.map((img, index) => (
                          <div key={index} className="relative w-20 h-20 bg-[#0A0A0A] rounded overflow-hidden group">
                            <img
                              src={img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img}
                              alt={`Mérettáblázat ${index + 1}`}
                              className="w-full h-full object-contain"
                            />
                            <button
                              type="button"
                              onClick={() => removeSizeChartImage(index)}
                              className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        <div className="w-20 h-20">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleSizeChartImageUpload(e.target.files[0])}
                            className="hidden"
                            id="size-chart-upload"
                          />
                          <label
                            htmlFor="size-chart-upload"
                            className="w-full h-full flex flex-col items-center justify-center border border-dashed border-white/30 rounded cursor-pointer hover:border-[#D4AF37] transition-colors"
                          >
                            <Plus size={18} className="text-white/40" />
                            <span className="text-white/40 text-xs mt-1">Kép</span>
                          </label>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="url"
                          id="sizechart-url-input"
                          className="bg-[#0A0A0A] border-white/20 text-white flex-1"
                          placeholder="Mérettáblázat kép URL (https://...)"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const url = e.target.value.trim();
                              if (url && url.startsWith('http')) {
                                setProductForm({
                                  ...productForm,
                                  size_chart_images: [...productForm.size_chart_images, url]
                                });
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-[#D4AF37] text-[#D4AF37]"
                          onClick={() => {
                            const input = document.getElementById('sizechart-url-input');
                            const url = input.value.trim();
                            if (url && url.startsWith('http')) {
                              setProductForm({
                                ...productForm,
                                size_chart_images: [...productForm.size_chart_images, url]
                              });
                              input.value = '';
                            }
                          }}
                        >
                          Hozzáad
                        </Button>
                      </div>
                    </div>

                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Leírás</label>
                      <textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                        rows={3}
                        className="w-full bg-[#0A0A0A] border border-white/20 rounded-md text-white p-3"
                        placeholder="Termék leírása..."
                      />
                    </div>

                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Mosási útmutató</label>
                      <Input
                        value={productForm.washing_instructions}
                        onChange={(e) => setProductForm({...productForm, washing_instructions: e.target.value})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                        placeholder="pl. 30°C-on mosható"
                      />
                    </div>

                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Alap ár (Ft)</label>
                      <Input
                        type="number"
                        value={productForm.base_price}
                        onChange={(e) => setProductForm({...productForm, base_price: parseInt(e.target.value) || 0})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                      />
                    </div>

                    <div>
                      <label className="text-white/60 text-sm mb-1 block">💰 Beszerzési ár (Ft) - csak admin látja</label>
                      <Input
                        type="number"
                        value={productForm.purchase_price}
                        onChange={(e) => setProductForm({...productForm, purchase_price: e.target.value === "" ? "" : parseInt(e.target.value)})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                        placeholder="Hagyd üresen ha nincs"
                      />
                      <p className="text-white/40 text-xs mt-1">A haszon számításához. Ha üres, az eladási árral számol (0 haszon).</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Készlet (db)</label>
                        <Input
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                          className="bg-[#0A0A0A] border-white/20 text-white"
                          placeholder="Üres = korlátlan"
                          min="0"
                        />
                        <p className="text-white/40 text-xs mt-1">Hagyd üresen ha korlátlan</p>
                      </div>
                      
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Státusz</label>
                        <button
                          type="button"
                          onClick={() => setProductForm({...productForm, is_active: !productForm.is_active})}
                          className={`w-full p-2 rounded-lg border flex items-center justify-center gap-2 transition-colors ${
                            productForm.is_active
                              ? "bg-green-500/20 border-green-500 text-green-400"
                              : "bg-red-500/20 border-red-500 text-red-400"
                          }`}
                        >
                          {productForm.is_active ? (
                            <>
                              <ToggleRight size={20} />
                              Aktív - Rendelhető
                            </>
                          ) : (
                            <>
                              <ToggleLeft size={20} />
                              Inaktív - Nem rendelhető
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Custom Name Option */}
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <label className="text-white font-medium">✏️ Egyedi név opció</label>
                          <p className="text-white/40 text-xs">A vásárló egyedi nevet adhat a termékhez extra díjért</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setProductForm({...productForm, custom_name_enabled: !productForm.custom_name_enabled})}
                          className={`p-2 rounded-lg border transition-colors ${
                            productForm.custom_name_enabled
                              ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                              : "border-white/20 text-white/40"
                          }`}
                        >
                          {productForm.custom_name_enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                        </button>
                      </div>
                      
                      {productForm.custom_name_enabled && (
                        <div className="grid grid-cols-2 gap-4 bg-[#0A0A0A] p-3 rounded-lg">
                          <div>
                            <label className="text-white/60 text-xs mb-1 block">Extra ár (Ft)</label>
                            <Input
                              type="number"
                              value={productForm.custom_name_price}
                              onChange={(e) => setProductForm({...productForm, custom_name_price: parseInt(e.target.value) || 0})}
                              className="bg-[#141414] border-white/20 text-white"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="text-white/60 text-xs mb-1 block">Max karakterek</label>
                            <Input
                              type="number"
                              value={productForm.custom_name_max_length}
                              onChange={(e) => setProductForm({...productForm, custom_name_max_length: parseInt(e.target.value) || 15})}
                              className="bg-[#141414] border-white/20 text-white"
                              min="1"
                              max="50"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Variation Groups */}
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <label className="text-white font-medium">Variációs csoportok</label>
                        <Button
                          type="button"
                          onClick={addVariationGroup}
                          variant="outline"
                          size="sm"
                          className="border-[#D4AF37] text-[#D4AF37]"
                        >
                          <Plus size={16} className="mr-1" />
                          Csoport
                        </Button>
                      </div>

                      {productForm.variation_groups.map((group, groupIndex) => (
                        <div key={group.id} className="bg-[#0A0A0A] rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Input
                              value={group.name}
                              onChange={(e) => updateVariationGroup(groupIndex, 'name', e.target.value)}
                              className="bg-[#141414] border-white/20 text-white flex-1"
                              placeholder="Csoport neve (pl. Méret, Szín)"
                            />
                            <button
                              type="button"
                              onClick={() => removeVariationGroup(groupIndex)}
                              className="text-red-500 hover:text-red-400 p-2"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>

                          <div className="space-y-2 ml-4">
                            {group.options.map((option, optionIndex) => (
                              <div key={option.id} className="bg-[#141414] p-3 rounded">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Input
                                    value={option.name}
                                    onChange={(e) => updateVariationOption(groupIndex, optionIndex, 'name', e.target.value)}
                                    className="bg-[#0A0A0A] border-white/20 text-white w-24"
                                    placeholder="Név"
                                  />
                                  <Input
                                    type="number"
                                    value={option.price}
                                    onChange={(e) => updateVariationOption(groupIndex, optionIndex, 'price', parseInt(e.target.value) || 0)}
                                    className="bg-[#0A0A0A] border-white/20 text-white w-20"
                                    placeholder="Ár"
                                  />
                                  <div className="flex items-center gap-1">
                                    <span className="text-white/40 text-xs">📦</span>
                                    <Input
                                      type="number"
                                      value={option.stock === null || option.stock === undefined ? "" : option.stock}
                                      onChange={(e) => updateVariationOption(groupIndex, optionIndex, 'stock', e.target.value === "" ? null : parseInt(e.target.value))}
                                      className="bg-[#0A0A0A] border-white/20 text-white w-16"
                                      placeholder="∞"
                                      min="0"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeVariationOption(groupIndex, optionIndex)}
                                    className="text-red-500 hover:text-red-400 p-1 ml-auto"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                                {/* Variation image upload */}
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  {option.image_url && (
                                    <div className="w-12 h-12 bg-[#0A0A0A] rounded overflow-hidden flex-shrink-0">
                                      <img
                                        src={option.image_url.startsWith("/api/") ? `${BACKEND_URL}${option.image_url}` : option.image_url}
                                        alt="Variáció kép"
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleVariationImageUpload(e.target.files[0], groupIndex, optionIndex)}
                                    className="hidden"
                                    id={`variation-image-${groupIndex}-${optionIndex}`}
                                  />
                                  <label
                                    htmlFor={`variation-image-${groupIndex}-${optionIndex}`}
                                    className="flex items-center gap-1 px-2 py-1 rounded border border-dashed border-white/30 text-white/60 text-xs cursor-pointer hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                                  >
                                    <Image size={14} />
                                    {option.image_url ? "Csere" : "Kép"}
                                  </label>
                                  {option.image_url && (
                                    <button
                                      type="button"
                                      onClick={() => updateVariationOption(groupIndex, optionIndex, 'image_url', '')}
                                      className="text-red-400 text-xs hover:text-red-300"
                                    >
                                      <X size={14} />
                                    </button>
                                  )}
                                  <Input
                                    type="url"
                                    value={option.image_url && !option.image_url.startsWith("/api/") ? option.image_url : ""}
                                    onChange={(e) => updateVariationOption(groupIndex, optionIndex, 'image_url', e.target.value)}
                                    className="bg-[#0A0A0A] border-white/20 text-white text-xs flex-1 min-w-[150px]"
                                    placeholder="vagy URL..."
                                  />
                                </div>
                              </div>
                            ))}
                            <Button
                              type="button"
                              onClick={() => addVariationOption(groupIndex)}
                              variant="ghost"
                              size="sm"
                              className="text-[#D4AF37] hover:text-[#F3E5AB]"
                            >
                              <Plus size={14} className="mr-1" />
                              Opció hozzáadása
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                      >
                        <Save size={18} className="mr-2" />
                        Mentés
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowProductForm(false);
                          resetProductForm();
                        }}
                        variant="outline"
                        className="border-white/20 text-white/60"
                      >
                        Mégse
                      </Button>
                    </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Products List */}
            <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg overflow-hidden">
              {products.length === 0 ? (
                <div className="p-12 text-center">
                  <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">Még nincs termék.</p>
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {products.map((product) => (
                    <div key={product.id} className={`p-4 flex items-center gap-4 ${!product.is_active ? "opacity-50" : ""}`}>
                      <div className="w-16 h-16 bg-[#0A0A0A] rounded overflow-hidden flex-shrink-0 relative">
                        {product.image_url ? (
                          <img
                            src={product.image_url.startsWith("/api/") ? `${BACKEND_URL}${product.image_url}` : product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-6 h-6 text-white/20" />
                          </div>
                        )}
                        {!product.is_active && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-red-400 text-xs font-bold">INAKTÍV</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-medium truncate">{product.name}</h3>
                          {!product.is_active && (
                            <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Inaktív</span>
                          )}
                        </div>
                        <p className="text-[#D4AF37]">{formatPrice(product.base_price)}</p>
                        <div className="flex gap-3 text-xs">
                          <span className={`${
                            product.stock === null || product.stock === undefined
                              ? "text-green-400"
                              : product.stock === 0
                              ? "text-red-400"
                              : product.stock <= 5
                              ? "text-yellow-400"
                              : "text-green-400"
                          }`}>
                            📦 {product.stock === null || product.stock === undefined ? "Korlátlan" : product.stock === 0 ? "Elfogyott" : `${product.stock} db`}
                          </span>
                          {product.variation_groups?.length > 0 && (
                            <span className="text-white/40">
                              {product.variation_groups.map(g => g.name).join(", ")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditProduct(product)}
                          className="p-2 text-white/60 hover:text-[#D4AF37]"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 text-white/60 hover:text-red-500"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <>
            {/* Order Statistics */}
            <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-4 mb-4">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <TrendingUp size={20} className="text-[#D4AF37]" />
                  Összesítő
                </h3>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-white/60">Időszak:</span>
                  <Input
                    type="date"
                    value={statsDateFrom}
                    onChange={(e) => setStatsDateFrom(e.target.value)}
                    className="bg-[#0A0A0A] border-white/20 text-white w-auto text-sm py-1"
                  />
                  <span className="text-white/40">-</span>
                  <Input
                    type="date"
                    value={statsDateTo}
                    onChange={(e) => setStatsDateTo(e.target.value)}
                    className="bg-[#0A0A0A] border-white/20 text-white w-auto text-sm py-1"
                  />
                </div>
                <div className="flex gap-2 ml-auto">
                  <Button
                    onClick={handleProcessExpiredOrders}
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-500/10"
                  >
                    <Clock size={18} className="mr-2" />
                    Lejárt rendelések
                  </Button>
                  <Button
                    onClick={() => setShowExportModal(true)}
                    variant="outline"
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    <FileSpreadsheet size={18} className="mr-2" />
                    Excel Export
                  </Button>
                </div>
              </div>
              
              {(() => {
                const stats = calculateOrderStats();
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#0A0A0A] rounded-lg p-3">
                      <p className="text-white/40 text-xs mb-1">Rendelések száma</p>
                      <p className="text-white text-xl font-bold">{stats.orderCount} db</p>
                    </div>
                    <div className="bg-[#0A0A0A] rounded-lg p-3">
                      <p className="text-white/40 text-xs mb-1">Bevétel (fizetendő)</p>
                      <p className="text-[#D4AF37] text-xl font-bold">{formatPrice(stats.revenue)}</p>
                    </div>
                    <div className="bg-[#0A0A0A] rounded-lg p-3">
                      <p className="text-white/40 text-xs mb-1">Költség (beszerzés)</p>
                      <p className="text-white/60 text-xl font-bold">{formatPrice(stats.totalCost)}</p>
                    </div>
                    <div className="bg-[#0A0A0A] rounded-lg p-3">
                      <p className="text-white/40 text-xs mb-1">Haszon</p>
                      <p className={`text-xl font-bold ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatPrice(stats.profit)}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Export Modal */}
            {showExportModal && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                <div className="bg-[#141414] rounded-lg max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold text-lg">Excel Export</h3>
                    <button onClick={() => setShowExportModal(false)} className="text-white/60 hover:text-white">
                      <X size={24} />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Időszak</label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          value={exportDateFrom}
                          onChange={(e) => setExportDateFrom(e.target.value)}
                          className="bg-[#0A0A0A] border-white/20 text-white flex-1"
                        />
                        <span className="text-white/40">-</span>
                        <Input
                          type="date"
                          value={exportDateTo}
                          onChange={(e) => setExportDateTo(e.target.value)}
                          className="bg-[#0A0A0A] border-white/20 text-white flex-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Termékek (üres = összes)</label>
                      <div className="max-h-40 overflow-y-auto bg-[#0A0A0A] rounded-lg p-2 space-y-1">
                        {products.map(product => (
                          <label key={product.id} className="flex items-center gap-2 text-white/80 text-sm cursor-pointer hover:text-white">
                            <input
                              type="checkbox"
                              checked={exportProducts.includes(product.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setExportProducts([...exportProducts, product.id]);
                                } else {
                                  setExportProducts(exportProducts.filter(id => id !== product.id));
                                }
                              }}
                              className="rounded"
                            />
                            {product.name}
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <Button
                      onClick={generateExcelExport}
                      className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                    >
                      <FileSpreadsheet size={18} className="mr-2" />
                      Letöltés
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Order Edit Modal */}
            {editingOrder && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-[#141414] rounded-lg max-w-lg w-full p-6 my-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold text-lg">Rendelés szerkesztése</h3>
                    <button onClick={() => setEditingOrder(null)} className="text-white/60 hover:text-white">
                      <X size={24} />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <p className="text-[#D4AF37] text-sm">{editingOrder.order_number}</p>
                    
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Név</label>
                      <Input
                        value={orderEditForm.customer_name}
                        onChange={(e) => setOrderEditForm({...orderEditForm, customer_name: e.target.value})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Email</label>
                      <Input
                        type="email"
                        value={orderEditForm.customer_email}
                        onChange={(e) => setOrderEditForm({...orderEditForm, customer_email: e.target.value})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Telefon</label>
                      <Input
                        value={orderEditForm.customer_phone}
                        onChange={(e) => setOrderEditForm({...orderEditForm, customer_phone: e.target.value})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Szállítási cím</label>
                      <Input
                        value={orderEditForm.customer_address}
                        onChange={(e) => setOrderEditForm({...orderEditForm, customer_address: e.target.value})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Megjegyzés</label>
                      <textarea
                        value={orderEditForm.notes}
                        onChange={(e) => setOrderEditForm({...orderEditForm, notes: e.target.value})}
                        rows={2}
                        className="w-full bg-[#0A0A0A] border border-white/20 rounded-md text-white p-3"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveOrder}
                        className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                      >
                        <Save size={18} className="mr-2" />
                        Mentés
                      </Button>
                      <Button
                        onClick={() => setEditingOrder(null)}
                        variant="outline"
                        className="border-white/20 text-white/60"
                      >
                        Mégse
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Orders List */}
            <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg overflow-hidden">
            {orders.filter(o => o.status !== "deleted" && o.status !== "törölve" && o.status !== "cancelled").length === 0 ? (
              <div className="p-12 text-center">
                <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60">Még nincs aktív rendelés.</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {orders.filter(o => o.status !== "deleted" && o.status !== "törölve" && o.status !== "cancelled").map((order) => (
                  <div key={order.id} className="p-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-white font-medium">{order.customer_name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status, order.pickup_location)}
                          </span>
                          {order.payment_reminder_sent && order.status !== "paid" && (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">
                              ⏰ 12h fizetés
                            </span>
                          )}
                        </div>
                        <p className="text-white/40 text-sm">{formatDate(order.created_at)}</p>
                        <p className="text-white/30 text-xs">{order.order_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] font-bold">{formatPrice(order.total_amount || order.total_price)}</p>
                        <div className="flex flex-col gap-1 mt-1 items-end">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditOrder(order)}
                              className="p-1 text-white/40 hover:text-[#D4AF37] transition-colors"
                              title="Szerkesztés"
                            >
                              <Edit size={16} />
                            </button>
                            <select
                              value={order.status}
                              onChange={(e) => {
                                if (e.target.value === "ready_pickup") {
                                  // Don't change yet, let user pick location
                                } else {
                                  handleUpdateOrderStatus(order.id, e.target.value);
                                }
                              }}
                              className="bg-[#0A0A0A] border border-white/20 rounded text-white text-sm p-1"
                            >
                              <option value="order_received">Rendelés felvéve</option>
                              <option value="payment_pending">Fizetés folyamatban</option>
                              <option value="paid">Fizetett</option>
                              <option value="payment_failed">Fizetés sikertelen</option>
                              <option value="preparing">Rendelés készülőben</option>
                              <option value="ready_pickup">Átvehető</option>
                              <option value="completed">Átvette</option>
                              <option value="cancelled">Törölve</option>
                            </select>
                          </div>
                          {order.status !== "ready_pickup" && order.status !== "completed" && order.status !== "cancelled" && (
                            <button
                              onClick={() => handleSetReadyPickup(order.id, "both")}
                              className="px-3 py-1.5 text-xs bg-[#D4AF37] text-[#0A0A0A] rounded hover:bg-[#F3E5AB] transition-colors font-medium"
                              title="Átvehető értesítés küldése"
                            >
                              📍 Átvehető
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-[#0A0A0A] rounded p-3 text-sm">
                      <p className="text-white/60 mb-1">
                        <strong>Email:</strong> {order.customer_email}
                      </p>
                      <p className="text-white/60 mb-1">
                        <strong>Telefon:</strong> {order.customer_phone}
                      </p>
                      <p className="text-white/60 mb-2">
                        <strong>Cím:</strong> {order.customer_address}
                      </p>
                      {order.notes && (
                        <p className="text-white/60 mb-2">
                          <strong>Megjegyzés:</strong> {order.notes}
                        </p>
                      )}
                      {order.coupon_code && (
                        <p className="text-green-400 mb-2">
                          <strong>Kupon:</strong> {order.coupon_code} (-{formatPrice(order.discount_amount || 0)})
                        </p>
                      )}
                      <div className="border-t border-white/10 pt-2 mt-2">
                        <strong className="text-white/60">Termékek:</strong>
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="text-white/80 ml-2">
                            • {item.product_name} x{item.quantity} - {formatPrice(item.price * item.quantity)}
                            {Object.keys(item.variation_selections || {}).length > 0 && (
                              <span className="text-white/40 ml-1">
                                ({Object.entries(item.variation_selections).map(([k, v]) => `${k}: ${v}`).join(", ")})
                              </span>
                            )}
                            {item.custom_name && (
                              <div className="text-[#D4AF37] ml-4 text-sm">
                                ✏️ Egyedi név: <strong>"{item.custom_name}"</strong>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </>
        )}

        {/* Coupons Tab */}
        {activeTab === "coupons" && (
          <>
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => {
                  resetCouponForm();
                  setShowCouponForm(true);
                }}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
              >
                <Plus size={18} className="mr-2" />
                Új kupon
              </Button>
            </div>

            {/* Coupon Form Modal */}
            {showCouponForm && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-[#141414] rounded-lg max-w-lg w-full my-8">
                  <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#141414] z-10">
                    <h2 className="text-xl font-bold text-white">
                      {editingCoupon ? "Kupon szerkesztése" : "Új kupon"}
                    </h2>
                    <button
                      onClick={() => {
                        setShowCouponForm(false);
                        resetCouponForm();
                      }}
                      className="text-white/60 hover:text-white"
                    >
                      <X size={24} />
                    </button>
                  </div>
                  
                  <form onSubmit={handleSaveCoupon} className="p-6 space-y-4">
                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Kuponkód *</label>
                      <Input
                        required
                        value={couponForm.code}
                        onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                        placeholder="pl. NYAR2025"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Kedvezmény típusa</label>
                        <select
                          value={couponForm.discount_type}
                          onChange={(e) => setCouponForm({...couponForm, discount_type: e.target.value})}
                          className="w-full bg-[#0A0A0A] border border-white/20 rounded-md text-white p-2"
                        >
                          <option value="percentage">Százalékos (%)</option>
                          <option value="fixed">Fix összeg (Ft)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">
                          Kedvezmény {couponForm.discount_type === "percentage" ? "(%)" : "(Ft)"}
                        </label>
                        <Input
                          type="number"
                          value={couponForm.discount_value}
                          onChange={(e) => setCouponForm({...couponForm, discount_value: parseInt(e.target.value) || 0})}
                          className="bg-[#0A0A0A] border-white/20 text-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Min. rendelés (Ft)</label>
                        <Input
                          type="number"
                          value={couponForm.min_order_amount}
                          onChange={(e) => setCouponForm({...couponForm, min_order_amount: parseInt(e.target.value) || 0})}
                          className="bg-[#0A0A0A] border-white/20 text-white"
                          placeholder="0 = nincs minimum"
                        />
                      </div>
                      <div>
                        <label className="text-white/60 text-sm mb-1 block">Max. felhasználás</label>
                        <Input
                          type="number"
                          value={couponForm.max_uses}
                          onChange={(e) => setCouponForm({...couponForm, max_uses: parseInt(e.target.value) || 0})}
                          className="bg-[#0A0A0A] border-white/20 text-white"
                          placeholder="0 = korlátlan"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-white/60 text-sm mb-1 block">Érvényesség vége (opcionális)</label>
                      <Input
                        type="date"
                        value={couponForm.valid_until}
                        onChange={(e) => setCouponForm({...couponForm, valid_until: e.target.value})}
                        className="bg-[#0A0A0A] border-white/20 text-white"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                      >
                        <Save size={18} className="mr-2" />
                        Mentés
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowCouponForm(false);
                          resetCouponForm();
                        }}
                        variant="outline"
                        className="border-white/20 text-white/60"
                      >
                        Mégse
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Coupons List */}
            <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg overflow-hidden">
              {coupons.length === 0 ? (
                <div className="p-12 text-center">
                  <Ticket className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">Még nincs kupon.</p>
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {coupons.map((coupon) => (
                    <div key={coupon.id} className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-mono font-bold text-lg">{coupon.code}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            coupon.is_active 
                              ? "bg-green-500/20 text-green-400" 
                              : "bg-red-500/20 text-red-400"
                          }`}>
                            {coupon.is_active ? "Aktív" : "Inaktív"}
                          </span>
                        </div>
                        <p className="text-[#D4AF37]">
                          {coupon.discount_type === "percentage" 
                            ? `${coupon.discount_value}% kedvezmény`
                            : `${formatPrice(coupon.discount_value)} kedvezmény`
                          }
                        </p>
                        <div className="text-white/40 text-xs mt-1 flex flex-wrap gap-x-4">
                          {coupon.min_order_amount > 0 && (
                            <span>Min: {formatPrice(coupon.min_order_amount)}</span>
                          )}
                          {coupon.max_uses > 0 && (
                            <span>Felhasználva: {coupon.current_uses || 0}/{coupon.max_uses}</span>
                          )}
                          {coupon.valid_until && (
                            <span>Lejár: {new Date(coupon.valid_until).toLocaleDateString('hu-HU')}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleCoupon(coupon.id)}
                          className={`p-2 ${coupon.is_active ? "text-green-400" : "text-white/40"} hover:text-[#D4AF37]`}
                          title={coupon.is_active ? "Inaktiválás" : "Aktiválás"}
                        >
                          {coupon.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                        </button>
                        <button
                          onClick={() => handleEditCoupon(coupon)}
                          className="p-2 text-white/60 hover:text-[#D4AF37]"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteCoupon(coupon.id)}
                          className="p-2 text-white/60 hover:text-red-500"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
